'use client';

import { useState, useEffect } from 'react';
import BookingStepIndicator from './BookingStepIndicator';
import ApartmentSelection from './ApartmentSelection';
import DateSelection from './DateSelection';
import GuestDetails from './GuestDetails';
import PaymentOptions from './PaymentOptions';
import BookingSummary from './BookingSummary';
import Icon from '@/components/ui/AppIcon';

interface Apartment {
  id: string;
  name: string;
  type: string;
  price: number;
  priceUSD: number;
  image: string;
  alt: string;
  features: string[];
  maxGuests: number;
  size: string;
}

const mockApartments: Apartment[] = [
{
  id: 'luxos-1',
  name: 'Luxos Luxury',
  type: '1 BHK Premium Suite',
  price: 4750,
  priceUSD: 51,
  image: "/assets/images/IMG20251021160224_copy-1767696990093.jpg",
  alt: 'Luxurious Luxos apartment with premium furnishings, wooden flooring, elegant beige walls, and pristine white bedding',
  features: [
  'Premium furnishings & designer decor',
  'Private modular kitchen',
  'Smart home automation',
  'Private balcony with city views'],

  occupancy: 'Up to 2 adults & 1 child <6 yo',
  size: '400 sq ft'
},
{
  id: 'primera-1',
  name: 'Primera Premium',
  type: '1 BHK Modern Comfort',
  price: 3850,
  priceUSD: 43,
  image: "/assets/images/IMG20251018151027_copy-1767697078477.jpg",
  alt: 'Modern Primera bedroom with wooden headboard featuring backlit design, pristine white bedding, ceiling fan, air conditioning, and beige walls',
  features: [
  'Modern comfort & stylish décor',
  'Compact modern kitchen',
  'High-speed WiFi',
  'Smart space design'],

  occupancy: 'Up to 2 adults & 1 child <6 yo',
  size: '400 sq ft'
},
{
  id: 'classica-1',
  name: 'Classica Comfort',
  type: '1 BHK Comfortable Living',
  price: 3100,
  priceUSD: 34,
  image: "/assets/images/IMG20251029135419_1copy-1767817703345.jpg",
  alt: 'Bright Classica studio apartment featuring white walls, ceiling fan, modern furnishings, comfortable bed with floral bedding, and golden curtains',
  features: [
  'Quality comfortable furnishings',
  'Well-equipped kitchen',
  'Dedicated work area',
  'High-speed WiFi'],

  maxGuests: 3,
  size: '400 sq ft'
}];


export default function BookingInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedApartment, setSelectedApartment] = useState<string | null>(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    guestType: '',
    adults: 1,
    children: 0,
    specialRequests: ''
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const steps = ['Select Apartment', 'Choose Dates', 'Guest Details', 'Payment'];

  const calculateNights = () => {
    if (checkIn && checkOut) {
      const start = new Date(checkIn);
      const end = new Date(checkOut);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  };

  const handleFormChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedApartment !== null;
      case 1:
        return checkIn !== '' && checkOut !== '' && calculateNights() > 0;
      case 2:
        return (
          formData.firstName !== '' &&
          formData.lastName !== '' &&
          formData.email !== '' &&
          formData.phone !== '' &&
          formData.guestType !== '');

      case 3:
        return paymentMethod !== '';
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = () => {
    setShowConfirmation(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-96 bg-[var(--color-muted)] rounded-lg animate-pulse" />
        </div>
      </div>);

  }

  const selectedApartmentData = mockApartments.find((apt) => apt.id === selectedApartment) || null;

  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-elevated p-8 text-center">
            <div className="w-20 h-20 bg-[var(--color-success)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={48} className="text-[var(--color-success)]" />
            </div>
            <h1 className="text-3xl font-display font-bold text-[var(--color-text-primary)] mb-4">
              Booking Confirmed!
            </h1>
            <p className="text-lg text-[var(--color-text-secondary)] mb-8">
              Thank you for choosing Proximus Serviced Apartments. Your booking has been successfully confirmed.
            </p>
            <div className="bg-[var(--color-muted)] rounded-xl p-6 mb-8 text-left">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
                Booking Details
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-secondary)]">Confirmation Number:</span>
                  <span className="font-semibold text-[var(--color-text-primary)]">PXM-2025-{Math.floor(Math.random() * 10000)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-secondary)]">Guest Name:</span>
                  <span className="font-semibold text-[var(--color-text-primary)]">
                    {formData.firstName} {formData.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-secondary)]">Email:</span>
                  <span className="font-semibold text-[var(--color-text-primary)]">{formData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-secondary)]">Apartment:</span>
                  <span className="font-semibold text-[var(--color-text-primary)]">
                    {selectedApartmentData?.name}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/homepage'}
                className="px-8 py-3 bg-[var(--color-accent)] text-white rounded-lg font-semibold hover:bg-[var(--color-accent)]/90 transition-all duration-300">

                Return to Home
              </button>
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  setCurrentStep(0);
                  setSelectedApartment(null);
                  setCheckIn('');
                  setCheckOut('');
                  setPaymentMethod('');
                  setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    company: '',
                    guestType: '',
                    adults: 1,
                    children: 0,
                    specialRequests: ''
                  });
                }}
                className="px-8 py-3 bg-white text-[var(--color-primary)] border-2 border-[var(--color-primary)] rounded-lg font-semibold hover:bg-[var(--color-primary)] hover:text-white transition-all duration-300">

                Make Another Booking
              </button>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-[var(--color-text-primary)] mb-4">
            Complete Your Booking
          </h1>
          <p className="text-lg text-[var(--color-text-secondary)] max-w-2xl mx-auto">
            Reserve your premium serviced apartment in just a few simple steps
          </p>
        </div>

        <BookingStepIndicator currentStep={currentStep} steps={steps} />

        <div className="grid lg:grid-cols-3 gap-8 mt-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-elevated p-8">
              {currentStep === 0 &&
              <ApartmentSelection
                apartments={mockApartments}
                selectedApartment={selectedApartment}
                onSelect={setSelectedApartment}
                currency={currency} />

              }
              {currentStep === 1 &&
              <DateSelection
                checkIn={checkIn}
                checkOut={checkOut}
                onCheckInChange={setCheckIn}
                onCheckOutChange={setCheckOut} />

              }
              {currentStep === 2 &&
              <GuestDetails formData={formData} onFormChange={handleFormChange} />
              }
              {currentStep === 3 &&
              <PaymentOptions
                paymentMethod={paymentMethod}
                onPaymentMethodChange={setPaymentMethod}
                currency={currency} />

              }

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--color-border)]">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="px-6 py-3 text-[var(--color-primary)] border-2 border-[var(--color-primary)] rounded-lg font-semibold hover:bg-[var(--color-primary)] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[var(--color-primary)]">

                  Back
                </button>
                {currentStep < steps.length - 1 ?
                <button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="px-8 py-3 bg-[var(--color-accent)] text-white rounded-lg font-semibold hover:bg-[var(--color-accent)]/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">

                    Continue
                  </button> :

                <button
                  onClick={handleSubmit}
                  disabled={!canProceed()}
                  className="px-8 py-3 bg-[var(--color-success)] text-white rounded-lg font-semibold hover:bg-[var(--color-success)]/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">

                    Confirm Booking
                  </button>
                }
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <BookingSummary
              selectedApartment={selectedApartmentData}
              checkIn={checkIn}
              checkOut={checkOut}
              nights={calculateNights()}
              currency={currency}
              onCurrencyChange={setCurrency} />

          </div>
        </div>
      </div>
    </div>);

}